import styled, { css } from 'styled-components'
import { mix } from 'polished'
import { breakpoint } from '@sketch/global-styles'
import { ReactComponent as Ellipsis } from '@sketch/icons/ellipsis-17'

import { ButtonUnstyled } from '../../Button'
import { truncateStyles } from '../../Truncate'

export const EllipsisIcon = styled(Ellipsis)`
  width: 17px;
  height: 17px;
`

export const DropdownAction = styled(ButtonUnstyled)`
  display: inline-flex;
  color: currentColor;

  height: 32px;

  justify-content: center;
  align-items: center;

  border-radius: ${({ theme }) => theme.radii.large};

  :hover,
  :focus,
  :focus-within {
    background-color: ${({ theme }) => theme.colors.background.tertiary.C};
  }
`

export const ActionWrapper = styled.div`
  /* Fix positioning issue with dropdown in Safari */
  position: relative;

  opacity: 0;
  width: 0;

  margin-left: auto;
  margin-right: 0;
  display: flex;
  justify-content: center;

  transition: ${({ theme: { transitions } }) =>
    css`
      opacity ${transitions.duration[2]} ${transitions.timing.easeInOut},
      width ${transitions.duration[2]} ${transitions.timing.easeInOut};
    `};
`

export const Icon = styled.svg`
  width: 17px;
  height: 17px;
  margin-right: 12px;
  flex-shrink: 0;

  /* This color should be "Light/colors.foreground.secondary.4" but given the icon composition */
  /* Opacity needs to be applied to the whole object */
  color: ${({ theme }) => theme.colors.foreground.secondary.A};
  opacity: 0.55;

  pointer-events: none;
`

const hoverStyle = css`
  background-color: ${({ theme }) => theme.colors.background.tertiary.C};
  color: ${({ theme }) => theme.colors.foreground.secondary.B};

  ${ActionWrapper} {
    width: 40px;
    opacity: 1;
  }

  ${DropdownAction} {
    width: 40px;
  }

  /* Fix the hover color of the icon */
  ${Icon} {
    color: ${({ theme }) => theme.colors.foreground.secondary.A};
    opacity: 1;
  }
`

// Used when an item is dragged over
const forceHoverStyle = css`
  background-color: ${({ theme }) => theme.colors.sketch.A};
  color: ${({ theme }) =>
    theme._name === 'dark' ? undefined : theme.colors.foreground.primary.A};

  /* Fix the hover color of the icon */
  ${Icon} {
    color: ${({ theme }) => theme.colors.foreground.primary.A};
    opacity: 1;
  }
`

export const HighlightIconWrapper = styled.div(
  ({ theme }) => css`
    position: relative;
    height: 17px;
    width: 17px;
    display: flex;
    align-items: center;
    margin-right: 12px;
    position: relative;

    &::after {
      content: '';
      background-color: ${theme.colors.sketch.A};
      position: absolute;
      top: -2px;
      right: -2px;
      border-radius: 50%;
      height: 8px;
      width: 8px;
      border: 1px solid ${theme.colors.background.secondary.B};
    }
  `
)

const activeStyle = css`
  background-color: ${({ theme }) => theme.colors.sketch.F};
  color: ${({ theme }) => theme.colors.sketch.A};

  ${Icon} {
    opacity: 1;

    /* Start animation for icon paths */
    path {
      animation-play-state: running;
    }
  }

  ${Icon}, ${ActionWrapper} {
    color: ${({ theme }) => theme.colors.sketch.A};
  }
`

export const menuLinkStyles = css`
  width: 100%;
  height: 40px;

  display: flex;
  align-items: center;

  background-color: transparent;

  padding: 8px 0px 8px 12px;
  border-radius: 5px;

  font-weight: ${({ theme }) => theme.fontWeights.regular};
  font-size: ${({ theme }) => theme.fontSizes.D};
  line-height: 1.2;

  color: ${({ theme }) => theme.colors.foreground.secondary.B};

  cursor: pointer;

  transition: none;

  ${breakpoint('sm')`
    height: 32px;
  `}

  ${ActionWrapper} {
    color: ${({ theme }) => theme.colors.foreground.secondary.D};
  }

  @media (hover: none) {
    ${ActionWrapper} {
      opacity: 1;
      width: 40px;
      min-width: 40px;
    }
  }

  :hover,
  :focus,
  :focus-within {
    ${hoverStyle};
  }

  &.active {
    ${activeStyle}
  }
`

export const Text = styled.span`
  ${truncateStyles};
  padding-right: 4px;

  /* Aligns the label with the icon */
  position: relative;
  top: 1px;

  pointer-events: none;
`

interface MenuLinkProps {
  $forceHover?: boolean
  $forceActive?: boolean
}

export const MenuLink = styled.div<MenuLinkProps>(
  ({ $forceHover, $forceActive }) => css`
    overflow: hidden;
    outline-color: transparent;

    ${menuLinkStyles};
    ${$forceActive ? activeStyle : ''};

    &,
    &.active {
      ${$forceHover && forceHoverStyle};

      &[data-being-dragged] {
        /**
          * We mix the background color with the sketch color
          * in order to create the same color with the expected opacity
          */
        background-color: ${({ theme }) =>
          mix(0.9, theme.colors.background.secondary.A, theme.colors.sketch.A)};
      }
    }

    &.active {
      ${Icon} {
        color: ${({ theme }) =>
          theme._name === 'dark'
            ? theme.colors.foreground.secondary.A
            : undefined};
      }
      color: ${({ theme }) =>
        theme._name === 'dark'
          ? theme.colors.foreground.secondary.A
          : undefined};
      background-color: ${({ theme }) =>
        theme._name === 'dark' ? theme.colors.sketch.A : undefined};
    }

    &[disabled] {
      color: ${({ theme }) => theme.colors.foreground.secondary.D};
    }

    &[data-focused='true'] {
      box-shadow: inset 0 0 0 2px ${({ theme }) => theme.colors.sketch.D};
    }

    &[data-being-dragged] {
      /**
        * We mix the background color with the sketch color
        * in order to create the same color with the expected opacity
        */
      background-color: ${({ theme }) =>
        mix(
          theme._name === 'dark' ? 1 - 0.16 : 1 - 0.04,
          theme.colors.background.secondary.A,
          theme._name === 'dark' ? 'white' : 'black'
        )};
    }
  `
)
